import chairSvg from '../assets/chair.svg?raw';
import { defineSketchObjectType, registerSketchObjectType } from '../registry';
import { SketchObjectAnchorKind, SketchObjectCategory, type SketchObjectFrame } from '../types';
import { at, pushRect, transformUnitSegments } from '../glyphs/frame';
import { beginGlyphInstance, endGlyphInstance } from '../glyphs/glyphInstances';
import { getUnitGlyph } from '../glyphs/svgGlyphs';
import type { ParamValues } from '../paramSchema';
import { t } from '../../translation/markers';

const PARAMS = {
  chair: { kind: 'boolean', label: t('chair'), default: true },
} as const;

/** Footprint of the chair symbol (matches the Chair type). */
const CHAIR_SIZE = 0.45;
/** Gap between the desk edge and the chair front. */
const CHAIR_GAP = 0.04;

/**
 * Desk: free-placed furniture (POINT anchor + FREE constraint — aligns to a
 * wall when placed against one, rotates with `R` otherwise). Worktop with a
 * light inner inset line (so it reads as a top, like the table) and an
 * opt-out chair symbol centered on the front edge. The back edge is at
 * local -Y — the side that rests against the wall when aligned.
 */
function buildDeskGlyph(frame: SketchObjectFrame, params: ParamValues<typeof PARAMS>): number[] {
  const out: number[] = [];
  const hw = frame.width / 2;
  const hd = frame.depth / 2;

  pushRect(out, frame, -hw, hw, -hd, hd);
  const inset = Math.min(frame.width, frame.depth) * 0.06;
  pushRect(out, frame, -hw + inset, hw - inset, -hd + inset, hd - inset);

  if (params.chair) {
    // One chair centered on the front (+depth/2) edge, facing the desk. The
    // chair SVG's backrest is at its local -Y, so the sub-frame's norm points
    // AT the desk (parent-local -Y) — same construction as the table's seats.
    const chairFrame: SketchObjectFrame = {
      center: at(frame, 0, hd + CHAIR_GAP + CHAIR_SIZE / 2),
      dir: { x: -frame.dir.x, y: -frame.dir.y },
      norm: { x: -frame.norm.x, y: -frame.norm.y },
      width: CHAIR_SIZE,
      depth: CHAIR_SIZE,
      z: frame.z,
    };
    beginGlyphInstance(out, 'chair', chairFrame);
    transformUnitSegments(getUnitGlyph('chair', chairSvg), chairFrame, out);
    endGlyphInstance(out);
  }

  return out;
}

registerSketchObjectType(
  defineSketchObjectType({
    id: 'desk',
    label: t('Desk'),
    category: SketchObjectCategory.FURNITURE,
    anchor: SketchObjectAnchorKind.POINT,
    constraint: { kind: 'FREE' },
    defaultSize: { width: 1.4, depth: 0.7 },
    params: PARAMS,
    glyph: { kind: 'parametric', build: buildDeskGlyph },
  }),
);
